import { useEffect, useState } from 'react';

interface RemindAtFieldProps {
  /** ISO instant, or null when no reminder is set. */
  value: string | null;
  onChange: (next: string | null) => void;
}

/**
 * When to be pinged about a task.
 *
 * `datetime-local` gives an offset-free wall clock, which is what a person means
 * by "9am on the 27th". It is interpreted in the *browser's* timezone, which is
 * correct for a Brussels-based user and visibly wrong for one abroad — so the
 * resolved time is echoed back underneath rather than left to be assumed.
 */
export function RemindAtField({ value, onChange }: RemindAtFieldProps) {
  const [local, setLocal] = useState(() => toLocalInput(value));

  useEffect(() => {
    setLocal(toLocalInput(value));
  }, [value]);

  const resolved = value ? new Date(value) : null;
  const isPast = resolved !== null && resolved.getTime() < Date.now();

  return (
    <div>
      <label className="mb-1 block text-body-xs font-medium text-text-secondary">Remind me</label>
      <div className="flex items-center gap-2">
        <input
          type="datetime-local"
          value={local}
          onChange={(e) => {
            setLocal(e.target.value);
            // An empty input means "no reminder", not "now".
            onChange(e.target.value ? new Date(e.target.value).toISOString() : null);
          }}
          className="w-full rounded-lg border border-border-light bg-surface-primary px-3 py-2 text-body-sm outline-none focus:border-text-primary"
        />
        {value && (
          <button
            type="button"
            onClick={() => {
              setLocal('');
              onChange(null);
            }}
            className="shrink-0 rounded-lg px-2 py-2 text-body-xs text-text-tertiary hover:text-error"
          >
            Clear
          </button>
        )}
      </div>

      {resolved && (
        <p className={`mt-1 text-body-xs ${isPast ? 'text-error' : 'text-text-tertiary'}`}>
          {isPast
            ? "That's in the past — no reminder will be sent."
            : `You'll get a Slack DM at ${formatResolved(resolved)}.`}
        </p>
      )}
    </div>
  );
}

/** An ISO instant as the `YYYY-MM-DDTHH:MM` the input wants, in local time. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatResolved(at: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(at);
}
